import _ from 'lodash';
import React, { memo } from 'react';
import {
  Box,
  Button,
  Input,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import messages from '../messages';
import { Controller } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import {
  AlertCriticalIcon,
  NotesIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';
import {
  Modal,
  useConfirmModal,
} from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import useIntl from '../../../hooks/useIntl';
import {
  AsyncBox,
  Banner,
} from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { calculateFields, functions, rawTemplate } from './narrativeTemplate';
import useSelectedProperty from '../../../hooks/useSelectedProperty';
import { extractByRegex } from 'utils/string-helpers';

const Comments = ({
  useFormReturn,
  fpRows,
  rentForecast,
  changeType,
  vacancyRateActual,
  isLoading,
  disableInput,
}) => {
  const fieldName = 'comments-section';
  const intl = useIntl();
  const property = useSelectedProperty();
  const currentComment = useFormReturn.watch(fieldName);
  const { openConfirm, openConfirmModal, closeConfirmModal } =
    useConfirmModal();
  const hasComment = !!currentComment?.trim();

  const onClickNarrative = () => {
    openConfirmModal();
  };

  const setNarrativeComment = () => {
    const formValues = useFormReturn.getValues();
    const processors = extractByRegex(rawTemplate, /\[\[(.+?)\]\]/g).reduce(
      (acc, fnName) => ({
        ...acc,
        [fnName]: _.has(functions, fnName)
          ? functions[fnName]({
              fpRows,
              rentForecast,
              changeType,
              formValues,
              property,
            })
          : null,
      }),
      {},
    );
    const fields = calculateFields({
      formValues,
      property,
      rentForecast,
      vacancyRateActual,
    });

    let result = rawTemplate.trim();

    for (const [processor, value] of _.entries(processors)) {
      if (value !== null) {
        result = result.replaceAll(`[[${processor}]]`, value);
      }
    }

    for (const [field, value] of _.entries(fields)) {
      result = result.replaceAll(`((${field}))`, value);
    }

    useFormReturn.setValue(fieldName, result);
  };

  return (
    <>
      <AsyncBox
        loading={isLoading}
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
        }}
      >
        <Box sx={{ width: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="titleLarge" my={2}>
              <FormattedMessage {...messages.comments} />
            </Typography>
            <Button
              variant="primarySubtle"
              startIcon={<NotesIcon />}
              sx={{ margin: 'auto 0' }}
              onClick={onClickNarrative}
              disabled={disableInput}
            >
              <FormattedMessage {...messages.commentsGenerate} />
            </Button>
          </Box>
          <Controller
            name={fieldName}
            control={useFormReturn.control}
            defaultValue=""
            render={({ field }) => (
              <Input
                {...field}
                placeholder="Start typing..."
                multiline
                rows={20}
                disabled={disableInput}
              />
            )}
          />
        </Box>
      </AsyncBox>
      <Modal
        title={intl.formatMessage(messages.narrativeModalTitle)}
        open={openConfirm}
        icon={<NotesIcon />}
        actionsProps={[
          {
            children: intl.formatMessage(messages.cancel),
            onClick: closeConfirmModal,
          },
          {
            children: intl.formatMessage(messages.confirmOnly),
            onClick: () => {
              setNarrativeComment();
              closeConfirmModal();
            },
          },
        ]}
        ModalProps={{
          PaperProps: {
            sx: {
              width: {
                xs: 'calc(100% - 32px)',
                sm: 480,
              },
              maxHeight: 'calc(100vh - 100px)',
            },
          },
        }}
      >
        <Typography>
          <FormattedMessage {...messages.narrativeModalDescription} />
        </Typography>
        {hasComment && (
          <Banner
            color="warning"
            text={<FormattedMessage {...messages.narrativeModalWarning} />}
            hasBorder={false}
            icon={<AlertCriticalIcon />}
            BoxProps={{
              sx: {
                marginTop: '20px',
                maxWidth: null,
                justifyContent: 'left',
              },
            }}
            TypographyProps={{
              whiteSpace: 'normal',
            }}
          />
        )}
      </Modal>
    </>
  );
};

export default memo(Comments);
